import React, { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import ServerSideTable from '@/components/shared/ServerSideTable';
import AuditLogTable from './AuditLogTable'; // adjust path to where you place the file
import { Receipt, Building2, ChevronDown, History } from 'lucide-react';
import branchService from '@/services/branchService';
import { useAuth } from '@/context/AuthContext';
import { P } from '@/config/permissionCodes';

export default function BillSummaryTable() {
  const { user, hasPermission } = useAuth();
  const dataUrl = "bills";

  // ─── RBAC ──────────────────────────────────────────────────────────────
  const canView   = hasPermission(P.BACK_OFFICE.BILLS.SCREEN);
  const canExport = hasPermission(P.BACK_OFFICE.BILLS.EXPORT);
  const canChangePaymentMode = hasPermission(P.BACK_OFFICE.BILLS.CHANGE_PAYMENT_MODE);
  const canReprint = hasPermission(P.BACK_OFFICE.BILLS.REPRINT);

  // Audit log visibility is tied to the Cancel Bill permission — anyone who can
  // cancel a bill needs to see the audit trail of cancellations too. No separate
  // permission grant required; the two travel together.
  const canViewAudit = hasPermission(P.BACK_OFFICE.BILLS.CANCEL);

  const isOwner = user?.userType === 'SUPER_USER';
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // 'bills' | 'audit'
  const [activeTab, setActiveTab] = useState('bills');

  // If the user's audit permission disappears mid-session (revoked by an admin,
  // or stale state from a previous login), force the tab back to Bills Summary
  // so the UI matches the gating below.
  useEffect(() => {
    if (!canViewAudit && activeTab === 'audit') {
      setActiveTab('bills');
    }
  }, [canViewAudit, activeTab]);

  useEffect(() => {
    if (!isOwner) return;
    const loadBranches = async () => {
      try {
        const result = await branchService.getAllBranches();
        if (result && result.length > 1) setBranches(result);
      } catch (err) {
        console.error('Failed to load branches:', err);
      }
    };
    loadBranches();
  }, [isOwner]);

  const additionalFilters = useMemo(() => {
    if (selectedBranchId) return { branchId: selectedBranchId };
    return {};
  }, [selectedBranchId]);

  // Access denied screen
  if (!canView) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full text-center px-6 py-20">
          <div>
            <p className="text-lg font-semibold text-gray-700">Access Denied</p>
            <p className="text-sm text-gray-500 mt-1">You don't have permission to view bills.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Effective audit view — must be both the current tab AND the user must have
  // permission. Used to drive header label/icon and content rendering so they
  // stay consistent even if state is stale.
  const isAudit = activeTab === 'audit';
  const showAuditView = isAudit && canViewAudit;

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm bg-gradient-to-br ${showAuditView ? 'from-indigo-500 to-indigo-600' : 'from-teal-500 to-teal-600'}`}>
                {showAuditView
                  ? <History className="w-5 h-5 text-white" />
                  : <Receipt className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {showAuditView ? 'Audit Log' : 'Bills Summary'}
                </h1>
                <p className="text-sm text-gray-500">
                  {showAuditView ? 'Track bill activity and changes' : 'View and manage all billing records'}
                </p>
              </div>
            </div>

            {/* Tab switcher — Audit tab only rendered for users with permission */}
            <div className="inline-flex p-1 bg-gray-100 rounded-lg">
              <TabButton active={!showAuditView} onClick={() => setActiveTab('bills')}>
                <Receipt size={15} />
                Bills Summary
              </TabButton>
              {canViewAudit && (
                <TabButton active={showAuditView} onClick={() => setActiveTab('audit')}>
                  <History size={15} />
                  Audit Log
                </TabButton>
              )}
            </div>
          </div>

          {branches.length > 1 && (
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={selectedBranchId ?? ''}
                onChange={(e) => setSelectedBranchId(e.target.value || null)}
                className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
              >
                <option value="">All Branches</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          )}
        </div>

        {/* Content — uses the same effective-view flag as the header so they're
            always in sync. Bills Summary is the default fallback. */}
        {showAuditView ? (
          <AuditLogTable branchId={selectedBranchId} />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <ServerSideTable
              tableName="bills-table"
              dataUrl={dataUrl}
              additionalFilters={additionalFilters}
              defaultFilters={{ finalizedAt: new Date().toISOString().split('T')[0] }}
              canExport={canExport}
              canChangePaymentMode={canChangePaymentMode}
              canReprint={canReprint}
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-md transition-colors ${
        active
          ? 'bg-white text-gray-900 shadow-sm'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}